import React, { useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { IconMoneybag, IconReceipt, IconWallet } from "@tabler/icons-react";

import RequestWithdrawalModal from "@components/_modals/Billing/RequestWithdrawalModal";
import TabsSelector from "@components/TabsSelector";
import { WALLET_PROVIDERS } from "@helpers/wallets";
import { HelpdeskSupport } from "@fragments";

import ConnectedAccount from "./components/ConnectedAccount";
import FundsProjection from "./components/FundsProjection";
import StripePayoutSummary from "./components/StripePayoutSummary";
import Taxes from "./components/Taxes";
import WithdrawalsTable from "./components/WithdrawalsTable";

import "./styles.scss";

const WITHDRAWAL_HELPDESK_ARTICLES = [
  {
    title: "Como criar planos para vender sua comunidade",
    subtitle: "Veja onde acessar e configurar seus planos.",
    url: "https://help.ginastee.com/pt-br/vendas-planos-e-cupons/como-criar-planos-para-vender-sua-comunidade#onde-acessar",
    icon: <IconReceipt />,
  },
  {
    title: "Como configurar sua Wallet no Ginastee",
    subtitle: "Entenda o status de analise da sua carteira.",
    url: "https://help.ginastee.com/pt-br/financeiro-wallet-e-saques/como-configurar-sua-wallet-no-ginastee#status-de-analise",
    icon: <IconWallet />,
  },
  {
    title: "Como solicitar um saque",
    subtitle: "Confira o passo a passo para pedir uma transferencia.",
    url: "https://help.ginastee.com/pt-br/financeiro-wallet-e-saques/como-solicitar-um-saque",
    icon: <IconMoneybag />,
  },
];

const PROVIDER_TABS = [
  { key: WALLET_PROVIDERS.ASAAS, label: "Nacional" },
  { key: WALLET_PROVIDERS.STRIPE, label: "Internacional" },
];

export default function WithdrawalSection({
  provider = WALLET_PROVIDERS.ASAAS,
  wallet,
  loadingWallet = false,
  reloadWallets,
  onProviderChange,
}) {
  const REF_modal = React.useRef();
  const { action } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const amountParam = searchParams.get("amount");

  async function handleLoad() {
    await reloadWallets?.();
  }

  useEffect(() => {
    if (action === "request" && REF_modal.current && wallet && !loadingWallet) {
      const requestedAmount = parseInt(amountParam, 10) || 0;
      navigate(`/banking/withdrawal?provider=${provider}`, { replace: true });
      REF_modal.current.open(
        {
          wallet,
          provider,
          amount:
            requestedAmount > 0
              ? requestedAmount
              : parseInt(wallet?.balance?.available, 10) || 0,
        },
        handleLoad,
      );
    }
  }, [action, wallet, loadingWallet, provider, amountParam]);

  function handleRegisterAccount() {
    if (REF_modal.current) {
      REF_modal.current.open(
        { configureOnly: true, provider, wallet },
        handleLoad,
      );
    }
  }

  const isStripe = provider === WALLET_PROVIDERS.STRIPE;

  return (
    <div className="withdrawals-screen">
      <div className="withdrawals-provider-tabs">
        <TabsSelector
          tabs={PROVIDER_TABS}
          value={provider}
          onChange={onProviderChange}
        />
      </div>

      <div className="wallet-info">
        <ConnectedAccount
          provider={provider}
          wallet={wallet}
          loading={loadingWallet}
          onRegisterAccount={handleRegisterAccount}
        />
        <Taxes provider={provider} wallet={wallet} loading={loadingWallet} />
      </div>

      {isStripe && (
        <StripePayoutSummary wallet={wallet} loading={loadingWallet} />
      )}

      <WithdrawalsTable provider={provider} currency={wallet?.currency} />

      {!isStripe && <FundsProjection />}

      <HelpdeskSupport.CardsGroup cards={WITHDRAWAL_HELPDESK_ARTICLES} />

      <RequestWithdrawalModal modalRef={REF_modal} />
    </div>
  );
}
